import Button from "components/buttons";
import Divider from "components/divider";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import FileUpload from "components/FileUpload";

import {
  createLegalResearchRequest,
  updateLegalResearchRequest,
} from "services/legalResearchService";
import {
  LEGAL_RESEARCH_REQUEST_STATUS,
  LEGAL_RESEARCH_REQUEST_TYPES,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { getTokenPayloadSafely } from "App";

interface TokenPayload {
  sub: string;
  exp: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  organization?: any;
}

const CreateNewRequest = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const contextData = useAuth();

  const location = useLocation();
  const editData = location.state?.request;

  const [formState, setFormState] = useState({
    letter_reference_number: editData?.letter_reference_number || "",
    reason: editData?.reason || "",
    research_topic: editData?.research_topic || "",
    research_topic_abstract: editData?.research_topic_abstract || "",
    letter_file: null as File | null,
    research_synopsis_file: null as File | null,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
  const fileInputRefSynopsis = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (
    file: File | null,
    fieldName: keyof Pick<
      typeof formState,
      "letter_file" | "research_synopsis_file"
    >,
  ) => {
    setFormState((prev) => ({ ...prev, [fieldName]: file }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formState.letter_reference_number)
      newErrors.letter_reference_number = t("LetterReferenceNumberRequired");
    if (!formState.reason)
      newErrors.reason = t("ReasonForLegalRequestRequired");
    if (!formState.research_topic)
      newErrors.research_topic = t("ResearchTopicRequired");
    if (!formState.research_topic_abstract)
      newErrors.research_topic_abstract = t("ResearchTopicAbstractRequired");
    if (!formState.letter_file)
      newErrors.letter_file = t("RequestLetterRequired");
    if (!formState.research_synopsis_file)
      newErrors.research_synopsis_file = t("ResearchSynopsisRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  console.log("editData", editData);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("PleaseFillAllRequiredFields"), "error");
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    Object.keys(formState).forEach((key) => {
      const value = formState[key as keyof typeof formState];
      if (value !== null)
        data.append(key, value instanceof File ? value : String(value));
    });

    const decoded = getTokenPayloadSafely(
      contextData.token || contextData.user.access_token || "",
    );

    if (!editData) {
      data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.pending.value);
      data.append("requested_by", contextData.user?.user_type);
      data.append("requested_by_id", decoded?.uu_id ?? "");
      data.append("stackholder_id", decoded?.organization?.uu_id || contextData?.user?.org_unit_id || "");
      data.append("first_name", decoded?.first_name || "");
      data.append("last_name", decoded?.last_name || "");
      data.append("email_user_organization", decoded?.email || "");
      data.append(
        "organization_kebele",
        decoded?.organization?.address?.kebele || "",
      );
      data.append(
        "organization_zone",
        decoded?.organization?.address?.zone || "",
      );
      data.append(
        "organization_state",
        decoded?.organization?.address?.state || "",
      );
      data.append("organization_name", decoded?.organization?.name || "");
      data.append("organization_type", decoded?.organization?.type || "");
      data.append(
        "organization",
        JSON.stringify({
          uu_id: decoded?.organization?.uu_id || contextData?.user?.org_unit_id || "",
        })
      );
    }

    if (editData && editData?.is_respondbacked_by_director) {
      data.append("status", LEGAL_RESEARCH_REQUEST_STATUS.pending.value);
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.forwarded_to_director.value,
      );
      data.append("is_respondbacked_by_director", "false");
    }

    try {
      let response;
      if (editData) {
        response = await updateLegalResearchRequest(
          editData.id,
          data,
          contextData.token || contextData.user.access_token || "",
        );
        showToast(t("LegalResearchRequestUpdatedSuccess"), "success");
      } else {
        response = await createLegalResearchRequest(
          contextData.token || contextData.user.access_token || "",
          data,
        );
        showToast(t("LegalResearchRequestSubmittedSuccess"), "success");
      }

      if (response.id) navigate(`/conduct-legal-research/${response.id}`);

      setFormState({
        letter_reference_number: "",
        reason: "",
        research_topic: "",
        research_topic_abstract: "",
        letter_file: null,
        research_synopsis_file: null,
      });
      if (fileInputRefRequestLetter.current)
        fileInputRefRequestLetter.current.value = "";
      if (fileInputRefSynopsis.current) fileInputRefSynopsis.current.value = "";
    } catch (error) {
      console.error(error);
      showToast(t("FailedToSubmitLegalResearchRequest"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageTitle
        icon="⚖"
        text={t("RequestForConductingLegalResearch")}
        subtitle={t("CreateNewRequestSubtitle")}
        iconColor="text-purple-600"
        textColor="text-[#432101]"
      />
      <form onSubmit={handleSubmit}>
        <TwoColumnLayout
          left={
            <div className="py-[22px] px-[24px] bg-white rounded-xl space-y-4">
              <Divider
                orientation="horizontal"
                text={t("ResearchDetail")}
                textPosition="left"
                textClassName="text-[#9D0303] font-[600] text-[18px]"
                className="my-4"
              />

              {/* First Row: Letter Reference Number and Reason */}
              <div className="grid gap-6 mb-6 md:grid-cols-2">
                <DynamicFormInput
                  inputs={[
                    {
                      value: formState.letter_reference_number,
                      type: "text",
                      label: t("LetterReferenceNumberRequired"),
                      name: "letter_reference_number",
                      onChange: handleChange,
                      placeholder: t("LetterReferenceNumberPlaceholder"),
                      error: errors.letter_reference_number,
                    },
                  ]}
                />
                <DynamicFormInput
                  inputs={[
                    {
                      value: formState.reason,
                      type: "select",
                      label: t("ReasonForLegalRequestRequired"),
                      name: "reason",
                      options: LEGAL_RESEARCH_REQUEST_TYPES,
                      onChange: handleChange,
                      placeholder: t("SelectReason"),
                      error: errors.reason,
                      rules: { required: true },
                    },
                  ]}
                />
              </div>

              {/* Research Topic & Abstract */}
              <DynamicFormInput
                inputs={[
                  {
                    value: formState.research_topic,
                    type: "textarea",
                    label: t("ResearchTopicRequired"),
                    name: "research_topic",
                    onChange: handleChange,
                    placeholder: t("EnterResearchTopic"),
                    error: errors.research_topic,
                  },
                  {
                    value: formState.research_topic_abstract,
                    type: "textarea",
                    label: t("ResearchTopicAbstractRequired"),
                    name: "research_topic_abstract",
                    onChange: handleChange,
                    placeholder: t("ProvideResearchTopicAbstract"),
                    style: { height: "15rem" },
                    error: errors.research_topic_abstract,
                  },
                ]}
              />

              {/* File Uploads */}
              <div className="flex flex-col md:flex-row gap-6 mt-4">
                <div className="flex-1">
                  <FileUpload
                    label={t("OfficialRequestLetterRequired")}
                    onChange={(file) => handleFileChange(file, "letter_file")}
                    ref={fileInputRefRequestLetter}
                  />
                  {errors.letter_file && (
                    <span className="text-red-500 text-sm">
                      {errors.letter_file}
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <FileUpload
                    label={t("ResearchSynopsisRequired")}
                    onChange={(file) =>
                      handleFileChange(file, "research_synopsis_file")
                    }
                    ref={fileInputRefSynopsis}
                  />
                  {errors.research_synopsis_file && (
                    <span className="text-red-500 text-sm">
                      {errors.research_synopsis_file}
                    </span>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex space-x-4 justify-between mt-4">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setFormState({
                      letter_reference_number: "",
                      reason: "",
                      research_topic: "",
                      research_topic_abstract: "",
                      letter_file: null,
                      research_synopsis_file: null,
                    });
                    if (fileInputRefRequestLetter.current)
                      fileInputRefRequestLetter.current.value = "";
                    if (fileInputRefSynopsis.current)
                      fileInputRefSynopsis.current.value = "";
                    navigate(-1);
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button type="submit" variant="primary">
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      {LoadingIcon} {t("Submitting")}
                    </div>
                  ) : (
                    t("SubmitRequest")
                  )}
                </Button>
              </div>
            </div>
          }
          right={
            <div className="w-full max-w-md space-y-4">
              <Notes
                title={t("ImportantNotes")}
                text={t("ImportantNotesText")}
                subtext={t("ImportantNotesSubtext")}
              />
              <div className="bg-white rounded-xl shadow-sm p-4 space-y-2">
                <h2 className="font-semibold">{t("DocumentRequirements")}</h2>
                <p className="text-sm text-gray-500">
                  {t("DocumentRequirementsDescription")}
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600">
                  <li>{t("OfficialRequestLetter")}</li>
                  <li>{t("ResearchSynopsis")}</li>
                  <li>{t("SupportingDocuments")}</li>
                </ul>
              </div>
            </div>
          }
        />
      </form>
    </div>
  );
};

export default CreateNewRequest;